/**
 * A cancellable timer list. Every looping asset schedules through one of these
 * instead of tracking its own timeouts, so stopping is a single call and no
 * callback from a half-finished cycle runs during the next one.
 */
export class Clock {
  private timers: number[] = [];

  at(ms: number, callback: () => void): void {
    // Dropped as it fires: every consumer reschedules through the same clock
    // and only calls clear() when it stops, so an id kept past its timeout
    // would stay in the list for as long as the section is in view.
    const id = window.setTimeout(() => {
      this.timers = this.timers.filter((timer) => timer !== id);
      callback();
    }, ms);
    this.timers.push(id);
  }

  clear(): void {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers = [];
  }
}

/**
 * Runs a loop only while its element is on screen. In a hidden tab setTimeout
 * clamps to one second, so an off-screen animation runs at one step per second
 * and is mid-cycle when it comes back into view.
 *
 * `threshold` defaults to 0, which the deck's cards require: a buried card is
 * scaled down and translated up so only a strip of it is in the viewport, and
 * against a fractional threshold its ratio crosses the threshold repeatedly,
 * restarting the loop at its first step each time.
 */
export const whileInView = (
  element: Element,
  start: () => void,
  stop: () => void,
  threshold = 0,
): (() => void) => {
  let running = false;
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry?.isIntersecting) {
        if (running) return;
        running = true;
        start();
      } else if (running) {
        running = false;
        stop();
      }
    },
    { threshold },
  );
  observer.observe(element);
  return () => {
    observer.disconnect();
    if (running) stop();
  };
};

/** Scales a stage drawn at a fixed width to the width of its column. */
export const fitStage = (
  wrap: HTMLElement,
  stage: HTMLElement,
  designWidth: number,
  minScale = 0,
): (() => void) => {
  const fit = (): void => {
    const width = wrap.clientWidth;
    if (!width) return;
    stage.style.transform = `scale(${Math.max(width / designWidth, minScale)})`;
  };
  fit();
  const observer = new ResizeObserver(fit);
  observer.observe(wrap);
  const listeners = new AbortController();
  window.addEventListener("resize", fit, { signal: listeners.signal });
  return () => {
    observer.disconnect();
    listeners.abort();
  };
};
